/**
 * Company API Service
 * Uses backend /api/companies/me/*
 */

import { ApiClient, API_BASE_URL } from './client';
import { TokenService } from '@/services/auth/TokenService';
import { withCompanyScope } from '@/services/companyScope';
import type { EnglishLevel } from '@/types/story.types';
import type {
  Employee,
  EmployeeFilters,
  CompanyMetrics,
  EmployeeProgress,
  ExportReportRequest,
  ExportReportResponse,
  DepartmentStats,
  CompanyRecentActivity,
} from '@/types/company.types';

export interface CompanyInfo {
  id: string;
  name: string;
  code: string;
  createdAt: string;
  totalEmployees: number;
  activeEmployees: number;
}

export async function getCompanyInfo(): Promise<CompanyInfo> {
  return ApiClient.get<CompanyInfo>(withCompanyScope('/companies/me'));
}

export async function getCompanyMetrics(): Promise<CompanyMetrics> {
  const data = await ApiClient.get<Record<string, unknown>>(withCompanyScope('/companies/me/metrics'));
  const topRaw = (data.topPerformers as Array<{ userId: string; name: string; totalXP: number; currentStreak?: number; longestStreak?: number }>) ?? [];
  const recentRaw = (data.recentActivity as Array<{ userName: string; storyTitle: string; completedAt: string; xpEarned: number }>) ?? [];

  const topPerformers: Employee[] = topRaw.map((p, i) => ({
    id: p.userId,
    userId: p.userId,
    name: p.name,
    email: '',
    // El top de la empresa siempre son alumnos (se ordena por XP).
    role: 'student',
    isCompanyAdmin: false,
    level: 'B1',
    isActive: true,
    registeredAt: '',
    lastActivityAt: '',
    stats: {
      totalXP: p.totalXP,
      currentStreak: p.currentStreak ?? 0,
      longestStreak: p.longestStreak ?? 0,
      storiesCompleted: 0,
      storiesInProgress: 0,
      averageScore: 0,
      totalTimeSpent: 0,
      rank: i + 1,
    },
  }));

  const recentActivity: CompanyRecentActivity[] = recentRaw.map((a, i) => ({
    id: `activity-${i}`,
    employeeName: a.userName,
    activityType: 'story_completed',
    description: `Completed "${a.storyTitle}" (+${a.xpEarned} XP)`,
    timestamp: a.completedAt,
  }));

  return {
    totalEmployees: (data.totalEmployees as number) ?? 0,
    activeEmployees: (data.activeEmployees as number) ?? 0,
    inactiveEmployees: (data.inactiveEmployees as number) ?? 0,
    averageLevel: (data.averageLevel as EnglishLevel) ?? 'A1',
    totalStoriesCompleted: (data.totalStoriesCompleted as number) ?? 0,
    totalXPEarned: (data.totalXPEarned as number) ?? 0,
    averageCompletionRate: (data.averageCompletionRate as number) ?? 0,
    averageTimeSpent: (data.averageTimeSpent as number) ?? 0,
    topPerformers,
    recentActivity,
  };
}

interface BackendEmployee {
  id: string;
  userId: string;
  name: string;
  email: string;
  role?: string;
  isCompanyAdmin?: boolean;
  level: string;
  department: string | null;
  position: string | null;
  isActive: boolean;
  registeredAt: string;
  lastActivityAt: string | null;
  stats?: { totalXP: number; storiesCompleted: number };
}

interface BackendEmployeeWithStats extends BackendEmployee {
  stats?: {
    totalXP: number;
    storiesCompleted: number;
    currentStreak?: number;
    longestStreak?: number;
    averageScore?: number;
    totalTimeSpent?: number;
  };
}

function mapBackendEmployeeToEmployee(be: BackendEmployeeWithStats, rank: number): Employee {
  return {
    id: be.id,
    userId: be.userId,
    name: be.name,
    email: be.email ?? '',
    role: (be.role as Employee['role']) ?? 'student',
    isCompanyAdmin: be.isCompanyAdmin ?? false,
    level: (be.level as Employee['level']) ?? 'A1',
    department: be.department ?? undefined,
    position: be.position ?? undefined,
    isActive: be.isActive ?? true,
    registeredAt: be.registeredAt ?? '',
    lastActivityAt: be.lastActivityAt ?? '',
    stats: {
      totalXP: be.stats?.totalXP ?? 0,
      currentStreak: be.stats?.currentStreak ?? 0,
      longestStreak: be.stats?.longestStreak ?? 0,
      storiesCompleted: be.stats?.storiesCompleted ?? 0,
      storiesInProgress: 0,
      averageScore: be.stats?.averageScore ?? 0,
      totalTimeSpent: be.stats?.totalTimeSpent ?? 0,
      rank,
    },
  };
}

export async function getEmployees(
  _filters?: EmployeeFilters,
  companyId?: string
): Promise<{ employees: Employee[]; total: number }> {
  const data = await ApiClient.get<{ employees: BackendEmployeeWithStats[]; total: number }>(
    withCompanyScope('/companies/me/employees', companyId)
  );
  const raw = data.employees ?? [];
  const employees = raw.map((e, i) => mapBackendEmployeeToEmployee(e, i + 1));
  return { employees, total: data.total ?? employees.length };
}

interface BackendEmployeeDetail extends Omit<BackendEmployee, 'stats'> {
  stats?: {
    totalXP: number;
    storiesCompleted: number;
    averageScore?: number;
    currentStreak?: number;
    longestStreak?: number;
    totalTimeSpent?: number;
  };
  storyHistory?: Array<{
    storyId: string;
    storyTitle: string;
    score: number;
    xpEarned: number;
    timeSpent?: number;
    completedAt: string;
    passed: boolean;
  }>;
}

/** Activar / desactivar un alumno de la empresa (solo company_admin). */
export async function setEmployeeActive(
  employeeId: string,
  isActive: boolean
): Promise<{ userId: string; isActive: boolean }> {
  return ApiClient.patch<{ userId: string; isActive: boolean }>(
    withCompanyScope(`/companies/me/employees/${employeeId}`),
    { isActive }
  );
}

export async function getEmployeeById(employeeId: string): Promise<Employee & { storyHistory?: unknown[] }> {
  const data = await ApiClient.get<BackendEmployeeDetail>(
    withCompanyScope(`/companies/me/employees/${employeeId}`)
  );
  const emp = mapBackendEmployeeToEmployee(
    {
      ...data,
      stats: {
        totalXP: data.stats?.totalXP ?? 0,
        storiesCompleted: data.stats?.storiesCompleted ?? 0,
        currentStreak: data.stats?.currentStreak,
        longestStreak: data.stats?.longestStreak,
        averageScore: data.stats?.averageScore,
        totalTimeSpent: data.stats?.totalTimeSpent,
      },
    } as BackendEmployeeWithStats,
    0
  );
  return {
    ...emp,
    storyHistory: data.storyHistory,
  } as Employee & { storyHistory?: unknown[] };
}

export async function getEmployeeProgress(employeeId: string): Promise<EmployeeProgress> {
  const emp = await getEmployeeById(employeeId);
  const rawHistory = (emp as { storyHistory?: unknown[] }).storyHistory ?? [];
  const storyHistory = rawHistory.map((h) => {
    const item = h as { storyId: string; storyTitle: string; score: number; xpEarned: number; timeSpent?: number; completedAt: string; passed: boolean };
    return {
      storyId: item.storyId,
      storyTitle: item.storyTitle,
      level: emp.level,
      status: item.passed ? ('completed' as const) : ('in_progress' as const),
      score: item.score,
      xpEarned: item.xpEarned,
      timeSpent: item.timeSpent ?? 0,
      completedAt: item.completedAt,
    };
  });
  return {
    employeeId,
    employeeName: emp.name,
    currentLevel: emp.level ?? 'A1',
    levelHistory: [],
    storyHistory,
    xpHistory: [],
    weeklyActivity: [],
  };
}

export interface CompanySkillStat {
  skill: string;
  achieved: number;
  total: number;
  pct: number;
}

export interface CompanyLearningStats {
  wordsLearnedThisMonth: number;
  skillBreakdown: CompanySkillStat[];
}

/**
 * Get learning stats for the company this month (vocabulary words + skill breakdown)
 */
export async function getCompanyLearningStats(): Promise<CompanyLearningStats> {
  return ApiClient.get<CompanyLearningStats>(withCompanyScope('/companies/me/learning-stats'));
}

/**
 * Get department statistics
 */
export async function getDepartmentStats(): Promise<DepartmentStats[]> {
  return ApiClient.get<DepartmentStats[]>(withCompanyScope('/companies/me/departments/stats'));
}

/**
 * Export company report (legacy - returns metadata)
 */
export async function exportReport(request: ExportReportRequest): Promise<ExportReportResponse> {
  return ApiClient.post<ExportReportResponse>(withCompanyScope('/companies/me/reports/export'), request);
}

async function downloadBlobFromEndpoint(
  endpoint: string,
  body: Record<string, unknown>,
  defaultFilename: string
): Promise<void> {
  const token = TokenService.getToken();
  if (!token) throw new Error('No authentication token found');

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const error = await response.json().catch(() => ({ error: 'Failed to download report' }));
    throw new Error((error as { error?: string }).error || 'Failed to download report');
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (contentDisposition) {
    const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
    if (matches?.[1]) filename = matches[1];
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function downloadReport(
  format: 'xlsx' | 'pdf',
  reportType: 'employees' | 'progress' | 'summary' = 'employees'
): Promise<void> {
  const date = new Date().toISOString().split('T')[0];
  await downloadBlobFromEndpoint(
    withCompanyScope('/companies/me/reports/export'),
    { format, reportType },
    `company-report-${reportType}-${date}.${format}`
  );
}

export async function downloadEmployeeReport(
  employeeId: string,
  format: 'xlsx' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<void> {
  const date = new Date().toISOString().split('T')[0];
  await downloadBlobFromEndpoint(
    withCompanyScope(`/companies/me/employees/${employeeId}/report`),
    { format, startDate, endDate },
    `employee-report-${employeeId}-${date}.${format}`
  );
}
